import { createContext, useContext, useMemo, useState, useEffect, useCallback } from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { blue, cyan, orange, grey, pink, purple, teal } from '@mui/material/colors';

const ThemeContext = createContext();

export const useThemeMode = () => useContext(ThemeContext);

// Temas disponíveis
export const THEME_MODES = {
  DEFAULT: 'default',
  DARK: 'dark',
  PASTEL: 'pastel',
  CITRICAS: 'citricas',
  VERMELHAS: 'vermelhas',
  PRETO_BRANCO: 'preto_branco'
};

// Cores específicas para cada tema
const THEME_COLORS = {
  [THEME_MODES.DEFAULT]: {
    primary: {
      main: blue[800], // #1976D2 - azul moderno
      light: blue[50], // #E3F2FD
      dark: blue[900],
    },
    secondary: {
      main: cyan[500], // #00BCD4 - ciano
      light: cyan[100],
      dark: cyan[700],
    },
    accent: {
      main: orange[500], // #FF9800 - laranja
    },
    warning: {
      main: orange[500], // #FF9800 - laranja
      light: orange[100],
      dark: orange[800],
    },
    info: {
      main: blue[500], // #2196F3 - azul
      light: blue[100],
      dark: blue[800],
    },
    error: {
      main: '#f44336', // Vermelho padrão
      light: '#ffcdd2',
      dark: '#d32f2f',
    },
    success: {
      main: '#4caf50', // Verde padrão
      light: '#c8e6c9',
      dark: '#2e7d32',
    },
    background: {
      default: grey[50], // #F5F5F5
      paper: '#FFFFFF',
    },
    text: {
      primary: grey[900], // #212121
      secondary: grey[700], // #757575
    },
    divider: grey[300], // #E0E0E0
  },
  [THEME_MODES.DARK]: {
    primary: {
      main: blue[400], // Azul mais claro para dark mode
      light: blue[200],
      dark: blue[600],
    },
    secondary: {
      main: cyan[400], // Ciano mais claro para dark mode
      light: cyan[200],
      dark: cyan[600],
    },
    accent: {
      main: orange[400], // Laranja mais claro para dark mode
    },
    warning: {
      main: orange[400], // Laranja mais claro para dark mode
      light: orange[200],
      dark: orange[600],
    },
    info: {
      main: blue[400], // Azul mais claro para dark mode
      light: blue[200],
      dark: blue[600],
    },
    error: {
      main: '#f44336', // Vermelho padrão
      light: '#ffcdd2',
      dark: '#d32f2f',
    },
    success: {
      main: '#4caf50', // Verde padrão
      light: '#c8e6c9',
      dark: '#2e7d32',
    },
    background: {
      default: "#121212",
      paper: "#1e1e1e",
    },
    text: {
      primary: "#ffffff",
      secondary: "rgba(255, 255, 255, 0.7)",
    },
    divider: grey[700],
  },
  [THEME_MODES.PASTEL]: {
    primary: {
      main: pink[300], // Rosa pastel suave
      light: pink[100],
      dark: pink[400],
    },
    secondary: {
      main: teal[300], // Verde-azulado pastel
      light: teal[100],
      dark: teal[400],
    },
    accent: {
      main: purple[300], // Roxo pastel
    },
    warning: {
      main: orange[300], // Laranja pastel
      light: orange[100],
      dark: orange[400],
    },
    info: {
      main: blue[300], // Azul pastel
      light: blue[100],
      dark: blue[400],
    },
    error: {
      main: '#f44336', // Vermelho padrão
      light: '#ffcdd2',
      dark: '#d32f2f',
    },
    success: {
      main: '#4caf50', // Verde padrão
      light: '#c8e6c9',
      dark: '#2e7d32',
    },
    background: {
      default: "#fefefe", // Branco com leve tom
      paper: '#f8f9fa',
    },
    text: {
      primary: "#4a4a4a", // Cinza escuro suave
      secondary: "#6c757d", // Cinza médio
    },
    divider: "#e9ecef", // Cinza muito claro
  },
  [THEME_MODES.CITRICAS]: {
    primary: {
      main: orange[600], // #FB8C00 - laranja vibrante
      light: orange[100], // #FFF3E0
      dark: orange[800], // #E65100
    },
    secondary: {
      main: '#FFD54F', // Amarelo dourado
      light: '#FFF9C4',
      dark: '#F57F17',
    },
    accent: {
      main: '#FF6F00', // Laranja intenso
    },
    warning: {
      main: '#FF6F00', // Laranja intenso
      light: '#FFF3E0',
      dark: '#E65100',
    },
    info: {
      main: '#FFD54F', // Amarelo dourado
      light: '#FFF9C4',
      dark: '#F57F17',
    },
    error: {
      main: '#f44336', // Vermelho padrão
      light: '#ffcdd2',
      dark: '#d32f2f',
    },
    success: {
      main: '#4caf50', // Verde padrão
      light: '#c8e6c9',
      dark: '#2e7d32',
    },
    background: {
      default: "#FFF8E1", // Amarelo muito claro
      paper: '#FFFFFF',
    },
    text: {
      primary: "#E65100", // Laranja escuro
      secondary: "#FF8F00", // Laranja médio
    },
    divider: "#FFCC02", // Amarelo dourado
  },
  [THEME_MODES.VERMELHAS]: {
    primary: {
      main: '#D32F2F', // Vermelho vibrante
      light: '#FFCDD2', // Vermelho muito claro
      dark: '#B71C1C', // Vermelho escuro
    },
    secondary: {
      main: '#E91E63', // Rosa vibrante
      light: '#F8BBD9',
      dark: '#AD1457',
    },
    accent: {
      main: '#C62828', // Vermelho intenso
    },
    warning: {
      main: '#FF8F00', // Laranja para warning
      light: '#FFE0B2',
      dark: '#E65100',
    },
    info: {
      main: '#E91E63', // Rosa vibrante
      light: '#F8BBD9',
      dark: '#AD1457',
    },
    error: {
      main: '#D32F2F', // Vermelho vibrante
      light: '#FFCDD2',
      dark: '#B71C1C',
    },
    success: {
      main: '#4caf50', // Verde padrão
      light: '#c8e6c9',
      dark: '#2e7d32',
    },
    background: {
      default: "#FFEBEE", // Vermelho muito claro
      paper: '#FFFFFF',
    },
    text: {
      primary: "#B71C1C", // Vermelho escuro
      secondary: "#D32F2F", // Vermelho médio
    },
    divider: "#EF5350", // Vermelho claro
  },
  [THEME_MODES.PRETO_BRANCO]: {
    primary: {
      main: '#000000', // Preto puro
      light: '#F5F5F5', // Cinza muito claro
      dark: '#000000', // Preto puro
    },
    secondary: {
      main: '#424242', // Cinza escuro
      light: '#E0E0E0', // Cinza claro
      dark: '#212121', // Cinza muito escuro
    },
    accent: {
      main: '#616161', // Cinza médio
    },
    warning: {
      main: '#616161', // Cinza médio
      light: '#E0E0E0',
      dark: '#424242',
    },
    info: {
      main: '#424242', // Cinza escuro
      light: '#E0E0E0',
      dark: '#212121',
    },
    error: {
      main: '#f44336', // Vermelho padrão
      light: '#ffcdd2',
      dark: '#d32f2f',
    },
    success: {
      main: '#4caf50', // Verde padrão
      light: '#c8e6c9',
      dark: '#2e7d32',
    },
    background: {
      default: "#FFFFFF", // Branco puro
      paper: '#FAFAFA', // Branco com leve tom
    },
    text: {
      primary: "#000000", // Preto puro
      secondary: "#424242", // Cinza escuro
    },
    divider: "#E0E0E0", // Cinza claro
  }
};

export function ThemeProvider({ children }) {
  // Carregar tema salvo no localStorage ou usar padrão
  const [themeMode, setThemeMode] = useState(() => {
    const savedTheme = localStorage.getItem('themeMode');
    // Validar se o tema salvo é válido
    if (savedTheme && THEME_COLORS[savedTheme]) {
      return savedTheme;
    }
    return THEME_MODES.DEFAULT;
  });

  // Salvar tema no localStorage sempre que mudar
  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
  }, [themeMode]);

  // Função para mudar tema (otimizada)
  const changeTheme = useCallback((newTheme) => {
    // Validar se o novo tema é válido
    if (THEME_COLORS[newTheme] && newTheme !== themeMode) {
      setThemeMode(newTheme);
    } else if (!THEME_COLORS[newTheme]) {
      console.warn(`Tema inválido: ${newTheme}, usando tema padrão`);
      setThemeMode(THEME_MODES.DEFAULT);
    }
  }, [themeMode]);

  // Função para alternar entre temas (mantida para compatibilidade)
  const toggleTheme = () => {
    const themes = Object.values(THEME_MODES);
    const currentIndex = themes.indexOf(themeMode);
    const nextIndex = (currentIndex + 1) % themes.length;
    setThemeMode(themes[nextIndex]);
  };

  const theme = useMemo(
    () => {
      // Garantir que colors nunca seja undefined
      const colors = THEME_COLORS[themeMode] || THEME_COLORS[THEME_MODES.DEFAULT];
      
      if (!colors) {
        console.error('Erro crítico: cores do tema não encontradas, usando tema padrão');
        return createTheme({
          palette: {
            mode: 'light',
            primary: { main: '#1976D2' },
            secondary: { main: '#00BCD4' },
          }
        });
      }
      
      return createTheme({
        palette: {
          mode: themeMode === THEME_MODES.DARK ? 'dark' : 'light',
          ...colors,
        },
        typography: {
          fontFamily: [
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
          ].join(','),
          h1: {
            fontSize: '2.5rem',
            fontWeight: 500,
            color: colors.text.primary,
          },
          h2: {
            fontSize: '2rem',
            fontWeight: 500,
            color: colors.text.primary,
          },
        },
        shape: {
          borderRadius: themeMode === THEME_MODES.PASTEL ? 16 : 8, // Bordas mais arredondadas para tema pastel
        },
        components: {
          MuiAppBar: {
            styleOverrides: {
              root: {
                boxShadow: themeMode === THEME_MODES.PASTEL 
                  ? '0 4px 20px rgba(0,0,0,0.08)' 
                  : '0 2px 4px rgba(0,0,0,0.08)',
                backgroundColor: colors.primary.main,
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                borderRight: `1px solid ${colors.divider}`,
                boxSizing: 'border-box',
                backgroundImage: "none",
                backgroundColor: colors.background.paper,
              },
            },
          },
          MuiButton: {
            defaultProps: {
              disableElevation: true,
            },
            styleOverrides: {
              root: {
                borderRadius: themeMode === THEME_MODES.PASTEL ? 20 : 8,
                textTransform: "none",
                fontWeight: 500,
                ...(themeMode === THEME_MODES.PASTEL && {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }),
              },
              containedPrimary: {
                '&:hover': {
                  backgroundColor: colors.primary.dark,
                  transform: themeMode === THEME_MODES.PASTEL ? 'translateY(-1px)' : 'none',
                  transition: themeMode === THEME_MODES.PASTEL ? 'all 0.2s ease' : 'none',
                },
              },
              outlinedPrimary: {
                borderWidth: themeMode === THEME_MODES.PASTEL ? '3px' : '2px',
                '&:hover': {
                  borderWidth: themeMode === THEME_MODES.PASTEL ? '3px' : '2px',
                  backgroundColor: colors.primary.light,
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: themeMode === THEME_MODES.PASTEL ? 20 : 12,
                boxShadow: themeMode === THEME_MODES.PASTEL 
                  ? '0 8px 25px rgba(0,0,0,0.08)' 
                  : '0 4px 12px rgba(0,0,0,0.06)',
                border: themeMode === THEME_MODES.PASTEL ? `1px solid ${colors.divider}` : 'none',
              },
            },
          },
          MuiTextField: {
            defaultProps: {
              variant: 'outlined',
              size: 'small',
            },
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: themeMode === THEME_MODES.PASTEL ? 12 : 8,
                  ...(themeMode === THEME_MODES.PASTEL && {
                    backgroundColor: '#ffffff',
                    borderColor: colors.primary.light,
                  }),
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
                ...(themeMode === THEME_MODES.PASTEL && {
                  border: `1px solid ${colors.divider}`,
                }),
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                borderRadius: themeMode === THEME_MODES.PASTEL ? 16 : 8,
                ...(themeMode === THEME_MODES.PASTEL && {
                  backgroundColor: colors.primary.light,
                  color: colors.primary.dark,
                }),
              },
            },
          },
        },
      });
    },
    [themeMode]
  );

  return (
    <ThemeContext.Provider value={{ 
      mode: themeMode, 
      changeTheme, 
      toggleTheme,
      availableThemes: THEME_MODES,
      currentTheme: themeMode
    }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

// Exportação padrão para compatibilidade
export default ThemeProvider;

